import React from 'react';
import { Points } from 'components/Points/Points';
import { OnboardingTask } from 'types/interfaces/onboarding';
import { ChipState } from './setupState';
import { CHIP_LABELS, LOCKED_NEEDS_ADDRESS, WAITING_CHIP_LABEL } from './setupCopy';

// One task chip on the ladder (Decision 13 and the chip visual states). Open,
// in-progress and ready chips are buttons; claimed, locked and waiting chips are
// not. The reward renders through <Points type="inline">, never as text.

const Icon: React.FC<{ state: ChipState }> = ({ state }) => {
  switch (state) {
    case 'claimed':
      return (
        <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
          <path d="M3 8.5 6.5 12 13 4.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'locked':
      return (
        <svg viewBox="0 0 16 16" width="11" height="11" aria-hidden="true">
          <rect x="3" y="7" width="10" height="7" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
          <path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" fill="none" stroke="currentColor" strokeWidth="1.6" />
        </svg>
      );
    case 'waiting':
      return (
        <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
          <path d="M2 7.5 14 2l-4.3 12-2.4-5L2 7.5z" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        </svg>
      );
    case 'inProgress':
      return (
        <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
          <circle cx="8" cy="8" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.6" opacity="0.35" />
          <path d="M8 2.5a5.5 5.5 0 0 1 5.5 5.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    case 'ready':
      return <span className="setup-chip__coin" aria-hidden="true" />;
    case 'open':
    default:
      return (
        <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
          <circle cx="8" cy="8" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
        </svg>
      );
  }
};

interface Props {
  task: OnboardingTask;
  state: ChipState;
  featured: boolean;
  /** Address-gated lock: the chip reads "needs address" */
  needsAddress: boolean;
  disabled?: boolean;
  onPick: (task: OnboardingTask) => void;
  onClaim: (task: OnboardingTask) => void;
}

const TaskChip: React.FC<Props> = ({ task, state, featured, needsAddress, disabled = false, onPick, onClaim }) => {
  const label = state === 'waiting' ? WAITING_CHIP_LABEL : CHIP_LABELS[task.taskKey];
  const clickable = state === 'open' || state === 'inProgress' || state === 'ready';
  const className = `setup-chip setup-chip--${state}${featured ? ' setup-chip--featured' : ''}`;
  const content = (
    <>
      <span className="setup-chip__icon">
        <Icon state={state} />
      </span>
      <span className="setup-chip__label">
        {label}
        {needsAddress && state === 'locked' && <span className="setup-chip__note"> · {LOCKED_NEEDS_ADDRESS}</span>}
      </span>
      {state !== 'claimed' && (
        <span className="setup-chip__reward">
          <Points type="inline" points={task.pointsReward} />
        </span>
      )}
    </>
  );
  if (!clickable) {
    return (
      <span className={className} data-testid={`chip-${task.taskKey}`} data-state={state} aria-disabled="true">
        {content}
      </span>
    );
  }
  return (
    <button
      type="button"
      className={className}
      data-testid={`chip-${task.taskKey}`}
      data-state={state}
      disabled={disabled}
      aria-pressed={featured || undefined}
      onClick={() => (state === 'ready' ? onClaim(task) : onPick(task))}
      title={state === 'ready' ? `Claim ${task.pointsReward} points for ${label}` : `Make ${label} the featured task`}
    >
      {content}
    </button>
  );
};

export default TaskChip;
